import React, { useState } from 'react';
import { Check, ArrowRight, Server, Briefcase, Layers, Info, X as LucideX } from 'lucide-react';
import ReactDOM from 'react-dom';
import { EXTERNAL_URLS } from '../config';

// Definições globais dos benefícios principais de cada plano
const BASIC_BENEFITS = [
  { section: null, text: '1 site', preview: 'Hospede um site com todos os recursos do plano.', showInfo: false },
  { section: null, text: 'Conecte seu próprio domínio', preview: 'Use um domínio que você já possui. O registro ou a transferência podem ser contratados separadamente.', showInfo: true },
  { section: null, text: '10 GB de armazenamento NVMe ultrarrápido', preview: 'Espaço suficiente para seu site, fotos, vídeos e arquivos, com tecnologia de alta velocidade para carregar tudo rapidamente.', showInfo: true },
  { section: null, text: 'Suporte para até 20.000 visitantes por mês', preview: 'Seu site pode receber até 20 mil acessos mensais sem perder desempenho.', showInfo: true },
  { section: null, text: '1 caixa de e-mail profissional', preview: 'Crie um e-mail personalizado com o nome do seu site, como contato@seudominio.com.', showInfo: true },
];
const PROFISSIONAL_BENEFITS = [
  { section: null, text: '50 sites', preview: 'Hospede até 50 sites com todos os recursos do plano.', showInfo: false },
  { section: null, text: 'Domínio grátis por 1 ano no plano pago', preview: 'O benefício de domínio elegível é ativado após a contratação. As condições de registro ou transferência são confirmadas no checkout.', showInfo: true },
  { section: null, text: '50 GB de armazenamento NVMe ultrarrápido', preview: 'Muito espaço e velocidade para sites profissionais.', showInfo: true },
  { section: null, text: 'Suporte para até 100.000 visitantes por mês', preview: 'Seu site pode receber até 100 mil acessos mensais com desempenho.', showInfo: true },
  { section: null, text: '5 caixas de e-mail profissional', preview: 'Crie até 5 e-mails personalizados para sua equipe.', showInfo: true },
];
const MAX_BENEFITS = [
  { section: null, text: '100 sites', preview: 'Hospede até 100 sites com todos os recursos do plano.', showInfo: false },
  { section: null, text: 'Domínio grátis por 1 ano no plano pago', preview: 'O benefício de domínio elegível é ativado após a contratação. As condições de registro ou transferência são confirmadas no checkout.', showInfo: true },
  { section: null, text: '100 GB de armazenamento NVMe ultrarrápido', preview: 'Espaço máximo e velocidade para grandes projetos.', showInfo: true },
  { section: null, text: 'Suporte para até 200.000 visitantes por mês', preview: 'Seu site pode receber até 200 mil acessos mensais com desempenho.', showInfo: true },
  { section: null, text: '10 caixas de e-mail profissional', preview: 'Crie até 10 e-mails personalizados para sua equipe.', showInfo: true },
];

// Definir BASIC_PLAN_SECTIONS no topo do arquivo, antes de EnhancedPricingPlan
const BASIC_PLAN_SECTIONS = [
  {
    title: null,
    items: [
      { text: 'Conecte seu próprio domínio', preview: 'Use um domínio que você já possui. O registro ou a transferência podem ser contratados separadamente.', showInfo: true },
      { text: '10 GB de armazenamento NVMe ultrarrápido', preview: 'Espaço suficiente para seu site, fotos, vídeos e arquivos, com tecnologia de alta velocidade para carregar tudo rapidamente.', showInfo: true },
      { text: 'Suporte para até 20.000 visitantes por mês', preview: 'Seu site pode receber até 20 mil acessos mensais sem perder desempenho.', showInfo: true },
      { text: '1 conta de e-mail profissional', preview: 'Crie um e-mail personalizado com o nome do seu site, como contato@seudominio.com.', showInfo: true },
      { text: '5 bancos de dados MySQL', preview: 'Crie até 5 bancos de dados para seus sites e aplicações.', showInfo: true },
    ]
  },
  {
    title: 'WordPress & Sites',
    items: [
      { text: 'Instalação de WordPress e outros CMS com 1 clique', preview: 'Instale plataformas populares para criar seu site sem precisar de programação.', showInfo: false },
      { text: 'Criador de sites Hostul incluso', preview: 'Monte seu site arrastando blocos, sem precisar saber programar.', showInfo: false },
      { text: 'Acesso a estatísticas de tráfego e uso em tempo real pelo painel', preview: 'Veja quantas pessoas visitam seu site e acompanhe o uso dos recursos.', showInfo: false },
    ]
  },
  {
    title: 'Segurança',
    items: [
      { text: 'Certificado SSL gratuito (Let\'s Encrypt)', preview: 'Garante que seu site seja seguro e confiável para os visitantes, ativando o cadeado de segurança.', showInfo: true },
      { text: 'Backups semanais', preview: 'Seu site é salvo automaticamente toda semana, protegendo contra perdas de dados.', showInfo: false },
      { text: 'Proteção básica contra ataques DDoS e monitoramento de disponibilidade 24/7', preview: 'Seu site fica protegido contra ataques e é monitorado o tempo todo para evitar quedas.', showInfo: true },
      { text: 'Proteção contra spam e antivírus para a conta de e-mail', preview: 'Seus e-mails ficam protegidos contra mensagens indesejadas e vírus.', showInfo: false },
    ]
  },
  {
    title: 'Suporte',
    items: [
      { text: 'Suporte técnico especializado 24/7 via chat e ticket', preview: 'Ajuda disponível a qualquer hora, todos os dias, para resolver dúvidas e problemas.', showInfo: false },
      { text: 'Painel de controle exclusivo Hostul', preview: 'Gerencie seu site, e-mails e recursos de forma fácil, mesmo sem experiência técnica.', showInfo: true },
    ]
  },
  {
    title: 'Recursos Técnicos',
    items: [
      { text: 'Recursos de cache e otimização de desempenho integrados', preview: 'Seu site carrega mais rápido, melhorando a experiência dos visitantes.', showInfo: false },
      { text: 'Contas FTP e Gerenciador de Arquivos avançado para administração completa', preview: 'Envie, organize e edite arquivos do seu site facilmente.', showInfo: false },
      { text: 'Gerenciador de DNS completo para configurar subdomínios e registros facilmente', preview: 'Controle endereços e subdomínios do seu site de forma simples.', showInfo: false },
    ]
  }
];

// Função utilitária para separar benefício
function splitBenefit(benefit) {
  const [main, ...rest] = benefit.split('—');
  return { main: main.trim(), extra: rest.join('—').trim() };
}

// Ícones originais para a Home
const PLAN_ICONS_HOME = {
  'Básico': Layers,
  'Profissional': Briefcase,
  'Hostul Max': Server
};
// Novo componente TooltipInfo para garantir exibição correta
function TooltipInfo({ info, alignRef }) {
  const [show, setShow] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const ref = React.useRef();

  React.useEffect(() => {
    if (show && alignRef && alignRef.current) {
      const alignRect = alignRef.current.getBoundingClientRect();
      // Posicionar o tooltip à direita do ícone, alinhado verticalmente ao centro
      const minWidth = 180;
      const maxWidth = 260;
      let top = alignRect.top + alignRect.height / 2;
      let left = alignRect.right + 8; // 8px de distância do ícone
      let tooltipWidth = Math.min(window.innerWidth - left - 16, maxWidth); // 16px margem direita
      if (tooltipWidth < minWidth) tooltipWidth = minWidth;
      // Se ultrapassar a borda direita, ajustar para caber
      if (left + tooltipWidth > window.innerWidth - 8) {
        left = window.innerWidth - tooltipWidth - 8;
      }
      // Se ultrapassar a borda esquerda, ajustar
      if (left < 8) left = 8;
      setTooltipPos({
        left,
        top: top - 20, // centralizar verticalmente em relação ao ícone
        width: tooltipWidth,
      });
    } else {
      setTooltipPos(null);
    }
  }, [show, alignRef]);

  return (
    <span className="relative ml-1">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        tabIndex={0}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        className="inline-flex"
        ref={alignRef}
      >
        <Info size={14} className="text-purple-400 cursor-pointer hover:text-purple-700" />
      </span>
      {show && tooltipPos && ReactDOM.createPortal(
        <span
          ref={ref}
          className="bg-white text-gray-900 text-xs rounded-lg px-3 py-2 shadow-lg whitespace-normal border border-purple-700"
          style={{
            position: 'fixed',
            left: tooltipPos.left,
            top: tooltipPos.top,
            minWidth: 180,
            maxWidth: 260,
            width: tooltipPos.width,
            zIndex: 9999,
            whiteSpace: 'normal',
            pointerEvents: 'none',
            transform: 'translateY(-50%)',
            boxShadow: '0 6px 32px 0 rgba(80, 0, 120, 0.18)',
          }}
        >
          {info}
        </span>,
        document.body
      )}
    </span>
  );
}

// Utilitário para scroll suave e profissional
function animateScrollTo(element, duration = 600, offset = 0) {
  if (!element) return;
  const startY = window.scrollY;
  const endY = element.getBoundingClientRect().top + window.scrollY + offset;
  const distance = endY - startY;
  let startTime = null;
  function step(currentTime) {
    if (!startTime) startTime = currentTime;
    const time = Math.min(1, (currentTime - startTime) / duration);
    const ease = 0.5 * (1 - Math.cos(Math.PI * time)); // easeInOut
    window.scrollTo(0, Math.round(startY + distance * ease));
    if (time < 1) {
      window.requestAnimationFrame(step);
    }
  }
  window.requestAnimationFrame(step);
}

// Componente de plano de preço aprimorado
export const EnhancedPricingPlan = ({ title, price48, total48, features, popular, icon: Icon, extraFeatures, showExtras, onToggleExtras, scrollToTitleRef, oldPrices, savings, planRenewalSubtitles, handleSelectPlan }) => {
  const [isHovered, setIsHovered] = useState(false);
  // Criar refs para cada benefício (para cada seção, se for o plano Básico)
  const featureRefs = React.useRef(features.map(() => React.createRef()));
  const extraRefs = React.useRef(extraFeatures ? extraFeatures.map(() => React.createRef()) : []);
  // Para o plano Básico, criar refs para cada item de cada seção
  const sectionRefs = React.useRef(
    title === 'Básico'
      ? BASIC_PLAN_SECTIONS.map(section => section.items.map(() => React.createRef()))
      : []
  );
  const cardRef = React.useRef();
  
  // Handler customizado para scroll ao ocultar
  const handleToggleExtras = () => {
    if (showExtras && scrollToTitleRef && scrollToTitleRef.current) {
      animateScrollTo(scrollToTitleRef.current, 600, 0);
      setTimeout(() => {
        onToggleExtras();
      }, 1000);
    } else {
      onToggleExtras();
    }
  };
  
  // Visual premium: fundo escuro, borda superior com gradiente, sombra sutil
  const cardBg =
    title === 'Hostul Max'
      ? 'bg-gradient-to-b from-gray-950 via-gray-950 to-black border border-purple-900 shadow-2xl'
      : 'bg-gradient-to-b from-gray-900 via-gray-950 to-gray-900 border border-purple-900 shadow-xl';
  
  const isProfessional = title === 'Profissional';
  const isTrialPlan = ['Básico', 'Profissional', 'Hostul Max'].includes(title);
  const buttonText = isTrialPlan ? `Testar ${title} grátis` : isProfessional ? 'Quero o Profissional' : 'Quero este plano';
  const buttonClass = isProfessional
    ? 'w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm md:text-base flex justify-center items-center mb-1 mt-1 scale-105'
    : 'w-full py-3 px-4 rounded-xl font-semibold text-purple-400 border-2 border-purple-600 transition-all duration-300 bg-transparent hover:bg-purple-900/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm md:text-base flex justify-center items-center mb-1 mt-1';

  // Função utilitária para gerar benefícios por plano, garantindo alinhamento total
  function getPlanSections(plan) {
    // Lista completa de todos os benefícios possíveis (nome, seção, preview, showInfo)
    const allBenefits = [
      // Seção inicial (sem título)
      { section: null, text: 'Domínio grátis por 1 ano no plano pago', preview: 'O benefício de domínio elegível é ativado após a contratação. As condições de registro ou transferência são confirmadas no checkout.', showInfo: true, plans: ['Profissional', 'Hostul Max'] },
      { section: null, text: '10 GB de armazenamento NVMe ultrarrápido', preview: 'Espaço suficiente para seu site, fotos, vídeos e arquivos, com tecnologia de alta velocidade para carregar tudo rapidamente.', showInfo: true, plans: ['Básico'], upgrade: { text: '50 GB de armazenamento NVMe ultrarrápido', plans: ['Profissional'] }, upgrade2: { text: '100 GB de armazenamento NVMe ultrarrápido', plans: ['Hostul Max'] } },
      { section: null, text: 'Suporte para até 20.000 visitantes por mês', preview: 'Seu site pode receber até 20 mil acessos mensais sem perder desempenho.', showInfo: true, plans: ['Básico'] },
      { section: null, text: '1 caixa de e-mail profissional', preview: 'Crie um e-mail personalizado com o nome do seu site, como contato@seudominio.com.', showInfo: true, plans: ['Básico'] },
      { section: null, text: '5 caixas de e-mail profissional', preview: 'Crie até 5 e-mails personalizados para sua equipe.', showInfo: true, plans: ['Profissional'] },
      { section: null, text: '10 caixas de e-mail profissional', preview: 'Crie até 10 e-mails personalizados para sua equipe.', showInfo: true, plans: ['Hostul Max'] },
      // WordPress & Sites
      { section: 'WordPress & Sites', text: '5 bancos de dados MySQL', preview: 'Crie bancos de dados para seus sites e aplicações conforme o limite do plano.', showInfo: true, plans: ['Básico'], upgrade: { text: '20 bancos de dados MySQL', plans: ['Profissional'] }, upgrade2: { text: 'Bancos de dados MySQL ilimitados', plans: ['Hostul Max'] } },
      { section: 'WordPress & Sites', text: 'Instalação de WordPress e outros CMS com 1 clique', preview: 'Instale plataformas populares para criar seu site sem precisar de programação.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'WordPress & Sites', text: 'Criador de sites Hostul incluso', preview: 'Monte seu site arrastando blocos, sem precisar saber programar.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'WordPress & Sites', text: 'Acesso a estatísticas de tráfego e uso em tempo real pelo painel', preview: 'Veja quantas pessoas visitam seu site e acompanhe o uso dos recursos.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      // Segurança
      { section: 'Segurança', text: 'Certificado SSL gratuito (Let\'s Encrypt)', preview: 'Garante que seu site seja seguro e confiável para os visitantes, ativando o cadeado de segurança.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Segurança', text: 'Backups semanais', preview: 'Seu site é salvo automaticamente toda semana, protegendo contra perdas de dados.', showInfo: false, plans: ['Básico'], upgrade: { text: 'Backups Diários', plans: ['Profissional', 'Hostul Max'] } },
      { section: 'Segurança', text: 'Proteção básica contra ataques DDoS e monitoramento de disponibilidade 24/7', preview: 'Seu site fica protegido contra ataques e é monitorado o tempo todo para evitar quedas.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Segurança', text: 'Proteção contra spam e antivírus para a conta de e-mail', preview: 'Seus e-mails ficam protegidos contra mensagens indesejadas e vírus.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      // Suporte
      { section: 'Suporte', text: 'Suporte técnico especializado 24/7 via chat e ticket', preview: 'Ajuda disponível a qualquer hora, todos os dias, para resolver dúvidas e problemas.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Suporte', text: 'Painel de controle exclusivo Hostul', preview: 'Gerencie seu site, e-mails e recursos de forma fácil, mesmo sem experiência técnica.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      // Recursos Técnicos
      { section: 'Recursos Técnicos', text: 'CDN Grátis', preview: 'Seu site é distribuído globalmente para carregar mais rápido em qualquer lugar.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Recursos Técnicos', text: 'Recursos de cache e otimização de desempenho integrados', preview: 'Seu site carrega mais rápido, melhorando a experiência dos visitantes.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Recursos Técnicos', text: 'Contas FTP e Gerenciador de Arquivos avançado para administração completa', preview: 'Envie, organize e edite arquivos do seu site facilmente.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Recursos Técnicos', text: 'Gerenciador de DNS completo para configurar subdomínios e registros facilmente', preview: 'Controle endereços e subdomínios do seu site de forma simples.', showInfo: false, plans: ['Básico', 'Profissional', 'Hostul Max'] },
      { section: 'Recursos Técnicos', text: 'Tráfego Ilimitado', preview: 'Receba quantos visitantes quiser, sem limites.', showInfo: true, plans: ['Profissional', 'Hostul Max'], unavailable: true },
    ];

    // Ocultar apenas benefícios legados que não fazem parte do catálogo atual.
    const filteredBenefits = allBenefits.filter(b => b.text !== 'Tráfego Ilimitado' && b.text !== 'Sites Ilimitados');

    // Montar as seções para o plano
    const sections = [];
    const sectionTitles = [null, 'WordPress & Sites', 'Segurança', 'Suporte', 'Recursos Técnicos'];
    sectionTitles.forEach(title => {
      const items = filteredBenefits.filter(b => {
        if (b.section !== title) return false;
        // Para caixas de e-mail, só incluir se o plano estiver em b.plans
        if (/caixa(s)? de e-mail profissional/.test(b.text)) {
          return b.plans.includes(plan);
        }
        return true;
      }).map(b => {
        let unavailable = false;
        let text = b.text;
        if (b.upgrade && b.upgrade.plans.includes(plan)) {
          text = b.upgrade.text;
        } else if (b.upgrade2 && b.upgrade2.plans.includes(plan)) {
          text = b.upgrade2.text;
        } else if (!b.plans.includes(plan)) {
          unavailable = true;
        }
        return { ...b, text, unavailable };
      });
      sections.push({ title, items });
    });
    return sections;
  }

  // No EnhancedPricingPlan, use os arrays de benefícios conforme o plano:
  let planSections;
  if (title === 'Básico') {
    planSections = [
      { title: null, items: BASIC_BENEFITS },
      ...getPlanSections('Básico').slice(1)
    ];
  } else if (title === 'Profissional') {
    planSections = [
      { title: null, items: PROFISSIONAL_BENEFITS },
      ...getPlanSections('Profissional').slice(1)
    ];
  } else if (title === 'Hostul Max' || title === 'Wordpress Max') {
    planSections = [
      { title: null, items: MAX_BENEFITS },
      ...getPlanSections('Hostul Max').slice(1)
    ];
  }
  
  // Lista de extras padronizada para todos os planos
  const ALL_EXTRA_FEATURES = [
    { text: 'Migração gratuita de site', preview: 'Transferimos seu site de outro provedor para a Hostul sem custo.', showInfo: true, plans: ['Profissional', 'Hostul Max'] },
    { text: 'Ambiente seguro com isolamento de recursos', preview: 'Seu site fica protegido de outros clientes no mesmo servidor.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
    { text: 'Atualizações automáticas de segurança', preview: 'Seu ambiente recebe correções de segurança automaticamente.', showInfo: true, plans: ['Profissional', 'Hostul Max'] },
    { text: 'Suporte a PHP, Node.js e Python', preview: 'Hospede sites e aplicações em diferentes linguagens.', showInfo: true, plans: ['Hostul Max'] },
    { text: 'Acesso SSH sob demanda', preview: 'Tenha acesso avançado ao servidor para tarefas técnicas.', showInfo: true, plans: ['Hostul Max'] },
    { text: 'Monitoramento de performance em tempo real', preview: 'Acompanhe o desempenho do seu site pelo painel.', showInfo: true, plans: ['Profissional', 'Hostul Max'] },
    { text: 'Ferramentas de SEO integradas', preview: 'Recursos para melhorar o posicionamento do seu site no Google.', showInfo: true, plans: ['Profissional', 'Hostul Max'] },
    { text: 'Garantia de uptime 99,9%', preview: 'Seu site sempre disponível, com alta confiabilidade.', showInfo: true, plans: ['Básico', 'Profissional', 'Hostul Max'] },
  ];
  // No EnhancedPricingPlan, use ALL_EXTRA_FEATURES para todos os planos
  const extraList = ALL_EXTRA_FEATURES.map(f => ({
    ...f,
    unavailable: !f.plans.includes(title)
  }));
  
  // Subtítulos amigáveis para cada plano
  const planSubtitles = {
    'Básico': 'Para começar com economia.',
    'Profissional': 'Para crescer com performance.',
    'Hostul Max': 'Para máxima liberdade e recursos.'
  };

  // Subtítulo de preço total e renovação por plano
  const defaultPlanRenewalSubtitles = {
    'Básico': 'Após o ciclo promocional, a renovação segue o preço vigente, informado antes do fim do período.',
    'Profissional': 'Após o ciclo promocional, a renovação segue o preço vigente, informado antes do fim do período.',
    'Hostul Max': 'Após o ciclo promocional, a renovação segue o preço vigente, informado antes do fim do período.'
  };
  const renewalSubtitles = planRenewalSubtitles || defaultPlanRenewalSubtitles;
  
  return (
    <div 
      ref={cardRef}
      className={`rounded-2xl overflow-hidden transform transition-all duration-500 ${cardBg} ${isHovered ? 'scale-105 shadow-2xl' : 'scale-100'} relative z-10 group focus-within:ring-2 focus-within:ring-purple-400`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
    >
      {/* Borda superior com gradiente e efeito de luz */}
      <div className="h-2 w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-700" />
      {/* Sombra superior para efeito de destaque */}
      <div className="absolute top-0 left-0 w-full h-8 bg-gradient-to-b from-purple-700/30 to-transparent pointer-events-none" />
      <div className="relative m-[2px] rounded-2xl bg-transparent h-full flex flex-col">
        <div className="p-6 md:p-7 flex-1 flex flex-col"> {/* Removido o padding-bottom extra */}
          {/* Cabeçalho */}
          <div className="flex items-center mb-1">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-purple-700 to-indigo-700 shadow-lg`}>
              <Icon size={22} className="text-white" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white ml-3 tracking-tight drop-shadow-sm">
              {title}
            </h3>
          </div>
          <div className="text-xs md:text-sm text-purple-200 font-medium mb-3 ml-1 md:ml-0 mt-2">
            {planSubtitles[title]}
          </div>
          {/* Preço 48 meses */}
          <div className="flex flex-col items-start mb-4 mt-2 relative h-auto w-full">
            <div className="flex items-center mb-1 flex-wrap gap-y-1">
              {isTrialPlan ? (
                <>
                  <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold shadow-sm border border-emerald-300/40 tracking-wide uppercase" style={{letterSpacing: '0.04em'}}>
                    Teste grátis por 14 dias
                  </span>
                  <span className="text-xs md:text-sm text-emerald-300 font-semibold ml-2">Sem cartão de crédito</span>
                </>
              ) : (
                <>
                  {/* Valor antigo riscado */}
                  <span className="text-base md:text-lg text-gray-400 line-through mr-2 select-none">{oldPrices[title]}</span>
                  {/* Caixa ECONOMIZE */}
                  <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-500 text-white text-xs font-bold shadow-sm border border-purple-400/40 tracking-wide uppercase" style={{letterSpacing: '0.04em'}}>{savings[title]}</span>
                </>
              )}
            </div>
            {isTrialPlan ? (
              <>
                <span className="text-3xl md:text-4xl font-bold text-purple-300 leading-tight">
                  14 dias grátis
                </span>
                <span className="text-[11px] md:text-xs text-gray-400 font-normal mt-1 mb-2 block">
                  Se você contratar depois: R$ {price48}/mês no ciclo promocional de 48 meses (total de R$ {total48}).
                </span>
              </>
            ) : (
              <>
                {/* Valor atual */}
                <span className="text-3xl md:text-4xl font-bold text-purple-300 leading-tight flex items-baseline">
                  <span className="text-base md:text-lg text-purple-200 mr-0.5">R$</span>{price48}<span className="text-xs md:text-sm text-gray-400 ml-0.5 font-normal">/mês</span>
                </span>
                <span className="text-[11px] md:text-xs text-gray-400 font-normal mt-1 mb-2 block">
                  Preço estimado considerando o ciclo promocional de 48 meses.
                </span>
              </>
            )}
          </div>
          {isTrialPlan && (
            <div className="mb-4 flex w-full items-start gap-2.5 rounded-lg bg-emerald-500/10 px-3 py-2.5 leading-relaxed">
              <Check size={16} className="mt-0.5 shrink-0 text-emerald-300" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold text-emerald-100 md:text-sm">
                  Recursos de hospedagem liberados
                </p>
                <p className="mt-0.5 text-[11px] text-emerald-100/90 md:text-xs">
                  Use sites, armazenamento, bancos de dados e e-mail profissional com os limites do plano {title}.
                </p>
                <p className="mt-1.5 text-[10px] text-gray-400 md:text-[11px]">
                  Registro e transferência de domínio ficam disponíveis após a contratação.
                </p>
              </div>
            </div>
          )}
          {/* Botão de ação acima dos benefícios */}
          <button
            className={buttonClass}
            onClick={() => handleSelectPlan && handleSelectPlan(title)}
          >
            {buttonText} <ArrowRight size={15} className="ml-2" />
          </button>
          {/* Subtítulo de renovação abaixo do botão */}
          <div className="text-[8.5px] md:text-[10px] text-gray-400 font-normal mt-1 mb-4 text-left w-full">
            {renewalSubtitles[title]}
          </div>
          <hr className="border-t border-gray-700/60 my-4 mb-5 w-full" />
          {isTrialPlan && (
            <div className="text-[10px] md:text-xs uppercase tracking-wider text-purple-300 font-bold mb-3">
              Recursos e limites do plano {title}
            </div>
          )}
          {/* Lista de recursos */}
          {title === 'Básico' || title === 'Profissional' || title === 'Hostul Max' || title === 'Wordpress Max' ? (
            <>
              {(() => {
                // Encontrar o índice da primeira seção com título
                const firstTitledSectionIdx = planSections.findIndex(s => s.title);
                return planSections.map((section, sidx) => {
                  // Apenas a primeira seção com título recebe mt-5, todas as demais mt-0
                  const sectionTitleClass = `text-xs uppercase tracking-wider text-purple-300 font-bold ${sidx === firstTitledSectionIdx ? 'mt-5' : 'mt-0'} mb-2`;
                  return (
                    <React.Fragment key={sidx}>
                      {section.title && <div className={sectionTitleClass}>{section.title}</div>}
                      <ul className="space-y-2 mb-5 flex-1">
                        {section.items.map((feature, index) => {
                          const ref = sectionRefs.current[sidx] ? sectionRefs.current[sidx][index] : undefined;
                          const unavailable = feature.unavailable;
                          return (
                            <li key={section.title + '-' + index} className={`flex items-start text-[15px] font-medium relative ${unavailable ? 'text-gray-500 line-through opacity-60' : 'text-gray-100'}`}> 
                              <div className={`p-1 rounded-full mt-0.5 mr-2 flex-shrink-0 flex items-center justify-center ${unavailable ? 'bg-gray-700' : 'bg-gradient-to-br from-purple-800/80 to-indigo-900/80'}`}>{unavailable ? <LucideX size={13} className="text-red-400" /> : <Check size={13} className="text-purple-300" />}</div>
                              <span className="flex items-center gap-1">
                                <span ref={ref}>{feature.text}</span>
                                {feature.showInfo && feature.preview && !unavailable && <TooltipInfo info={feature.preview} alignRef={ref} />}
                              </span>
              </li>
                          );
                        })}
          </ul>
                    </React.Fragment>
                  );
                });
              })()}
              {/* Substituir o botão de expandir/ocultar por um botão fixo no fundo do card */}
              {/* Renderizar os extras dentro do mesmo container */}
              {showExtras && (
                <>
                  <div className="text-xs uppercase tracking-wider text-purple-300 font-bold mt-5 mb-2">Benefícios Extras</div>
                  <ul className="space-y-2 mb-5 flex-1">
                    {extraList.map((feature, idx) => {
                      const ref = extraRefs.current[idx] || undefined;
                      return (
                        <li key={feature.text + '-' + idx} className={`flex items-start text-[15px] font-medium relative ${feature.unavailable ? 'text-gray-500 line-through opacity-60' : 'text-gray-300'}`}>
                          <div className={`p-1 rounded-full mt-0.5 mr-2 flex-shrink-0 flex items-center justify-center ${feature.unavailable ? 'bg-gray-700' : 'bg-gradient-to-br from-purple-900/80 to-indigo-900/80'}`}>{feature.unavailable ? <LucideX size={13} className="text-red-400" /> : <Check size={13} className="text-purple-400" />}</div>
                          <span className="flex items-center gap-1">
                            <span ref={ref}>{feature.text}</span>
                            {feature.showInfo && feature.preview && !feature.unavailable && <TooltipInfo info={feature.preview} alignRef={ref} />}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </>
          ) : (
            <ul className="space-y-2 mb-5 flex-1">
              {features.map((feature, index) => {
                const isObj = typeof feature === 'object';
                const main = isObj ? feature.text : splitBenefit(feature).main;
                const extra = isObj ? feature.preview : splitBenefit(feature).extra;
                const showInfo = isObj ? feature.showInfo : !!extra;
                return (
                  <li key={index} className="flex items-start text-gray-100 text-[15px] font-medium relative">
                    <div className="p-1 rounded-full bg-gradient-to-br from-purple-800/80 to-indigo-900/80 mt-0.5 mr-2 flex-shrink-0"><Check size={13} className="text-purple-300" /></div>
                    <span className="flex items-center gap-1">
                      <span ref={featureRefs.current[index]}>{main}</span>
                      {showInfo && extra && <TooltipInfo info={extra} alignRef={featureRefs.current[index]} />}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {/* Botão no fundo do card, fluxo normal */}
        <button
          onClick={handleToggleExtras}
          className="block w-full py-2.5 px-4 pb-5 text-center leading-none bg-gray-900/80 text-purple-300 text-xs font-semibold cursor-pointer rounded-b-2xl border-t border-purple-800 transition-colors hover:bg-purple-950/90"
        >
          {showExtras ? 'Ocultar benefícios' : 'Ver mais benefícios'}
        </button>
      </div>
    </div>
  );
};

// Seção completa de planos de preço
const PricingSection = () => {
  // Preços para 48 meses (exemplo, ajuste conforme necessário)
  const prices48 = {
    'Básico': '9,90',
    'Profissional': '19,90',
    'Hostul Max': '39,90'
  };
  const totals48 = {
    'Básico': '475,20',
    'Profissional': '955,20',
    'Hostul Max': '1.915,20'
  };

  // ✅ Valores riscados e economia real por plano (48 meses)
  const oldPrices = {
    'Básico': 'R$29,90/mês',
    'Profissional': 'R$54,90/mês',
    'Hostul Max': 'R$109,90/mês'
  };
  const savings = {
    'Básico': 'ECONOMIZE 67%', // Economia real para 48 meses
    'Profissional': 'ECONOMIZE 64%', // Economia real para 48 meses
    'Hostul Max': 'ECONOMIZE 64%' // Economia real para 48 meses
  };

  // Inicia o fluxo autenticado de teste grátis no painel.
  const handleSelectPlan = (planTitle) => {
    const titleToSlug = {
      'Básico': 'basico',
      'Profissional': 'profissional',
      'Hostul Max': 'max'
    };
    const slug = titleToSlug[planTitle];
    if (!slug) return;
    const trialUrl = new URL(`${EXTERNAL_URLS.panelBase}/trial`);
    trialUrl.searchParams.set('plan', slug);
    trialUrl.searchParams.set('source', 'home');
    window.location.assign(trialUrl.toString());
  };
  
  const [showExtras, setShowExtras] = useState(false);
  // 1. No PricingSection, crie um ref para o título
  const titleRef = React.useRef();
  
  return (
    <section id="planos" className="py-20 md:py-24 relative overflow-hidden">
      {/* Novo fundo escuro com gradiente e partículas */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 opacity-95"></div>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(#a78bfa_1px,transparent_1px)] [background-size:30px_30px] opacity-10"></div>
        <div className="absolute top-32 left-1/4 w-80 h-80 bg-purple-700 rounded-full filter blur-[120px] opacity-20"></div>
        <div className="absolute bottom-32 right-1/4 w-80 h-80 bg-indigo-700 rounded-full filter blur-[120px] opacity-20"></div>
      </div>
      <div className="container mx-auto px-2 md:px-4 relative z-10">
        <div className="text-center mb-8 md:mb-10">
          <h2 ref={titleRef} className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 text-white">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
              Planos que se Adaptam às Suas Necessidades
            </span>
          </h2>
          {/* Na seção de subtítulo, adicione espaçamento extra abaixo */}
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 md:mb-16">
            Do iniciante ao profissional, temos o plano perfeito para o seu projeto.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 max-w-6xl mx-auto">
          {/* Ordem: Básico | Profissional | Hostul Max */}
          <div id="plano-basico" className="flex flex-col md:mt-4">
            <EnhancedPricingPlan
              title="Básico"
              price48={prices48['Básico']}
              total48={totals48['Básico']}
              features={BASIC_BENEFITS}
              popular={false}
              icon={PLAN_ICONS_HOME['Básico']}
              showExtras={showExtras}
              onToggleExtras={() => setShowExtras(v => !v)}
              scrollToTitleRef={titleRef}
              oldPrices={oldPrices}
              savings={savings}
              handleSelectPlan={handleSelectPlan}
            />
          </div>
          <div className="flex flex-col md:mt-4">
            <div className="mb-2 -mt-4 relative flex justify-center">
              <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold shadow-md tracking-wide uppercase flash-effect">Escolha Recomendada</span>
              <style>{`
                .flash-effect {
                  position: relative;
                  overflow: hidden;
                }
                .flash-effect::after {
                  content: '';
                  position: absolute;
                  top: 0; left: -70%;
                  width: 80%; height: 100%;
                  background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 30%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.18) 70%, rgba(255,255,255,0) 100%);
                  filter: blur(2px);
                  transform: skewX(-18deg);
                  animation: flash-move 2.5s cubic-bezier(0.4,0,0.2,1) infinite;
                  pointer-events: none;
                }
                @keyframes flash-move {
                  0% { left: -70%; }
                  100% { left: 110%; }
                }
              `}</style>
            </div>
            <EnhancedPricingPlan
              title="Profissional"
              price48={prices48['Profissional']}
              total48={totals48['Profissional']}
              features={PROFISSIONAL_BENEFITS}
              popular={false}
              icon={PLAN_ICONS_HOME['Profissional']}
              showExtras={showExtras}
              onToggleExtras={() => setShowExtras(v => !v)}
              scrollToTitleRef={titleRef}
              oldPrices={oldPrices}
              savings={savings}
              handleSelectPlan={handleSelectPlan}
            />
          </div>
          <div className="flex flex-col md:mt-4">
            <EnhancedPricingPlan
              title="Hostul Max"
              price48={prices48['Hostul Max']}
              total48={totals48['Hostul Max']}
              features={MAX_BENEFITS}
              popular={false}
              icon={PLAN_ICONS_HOME['Hostul Max']}
              showExtras={showExtras}
              onToggleExtras={() => setShowExtras(v => !v)}
              scrollToTitleRef={titleRef}
              oldPrices={oldPrices}
              savings={savings}
              handleSelectPlan={handleSelectPlan}
            />
          </div>
        </div>
        <p className="max-w-4xl mx-auto mt-8 text-center text-xs md:text-sm text-gray-400 leading-relaxed">
          Teste disponível uma vez por conta, sem cartão e sem cobrança automática. Durante os 14 dias, os recursos técnicos
          e os limites do plano escolhido ficam integralmente disponíveis, incluindo sites, bancos de dados, armazenamento e
          e-mail profissional. O registro ou a transferência de domínio não fazem parte do teste e ficam disponíveis somente
          após a contratação de um plano pago elegível. Ao final do período gratuito, o serviço é pausado até uma contratação
          opcional. Os preços exibidos são vinculados ao ciclo promocional de 48 meses e confirmados no checkout.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
